import permissions from "app/permissions";
import React from "react";
import {
  Button,
  Datagrid,
  FunctionField,
  List,
  ReferenceField,
  SelectField,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  TopToolbar,
  useStore,
} from "react-admin";
import { useNavigate } from "react-router";
import AddIcon from "@mui/icons-material/Add";
import SecuredComponent from "server/security/SecuredComponent";
import { GraduationServicePath, MOBILE_BREAKPOINT, StateExamTypes } from "utils/constants";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import { StateExamAssignmentTypeChoices, StateExamTypeChoices } from "utils/choices";
import RoomField from "server/components/Fields/RoomField";
import CustomFilter from "features/CustomFilter";
import useInternshipFilter from "features/filters/useInternshipFilter";
import { InternshipSessionField } from "server/components/Fields/InternshipSessionInput";
import GradingIcon from "@mui/icons-material/Grading";
import { administrationRequests } from "api/administrationApi";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

const STORE_KEY = "internshipStateExams.filters";

const ListActions = () => {
  const navigate = useNavigate();

  return (
    <TopToolbar>
      <>
        {/* group exams */}
        <SecuredComponent permission={permissions.canCreateGroupStateExams}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => navigate(`/${GraduationServicePath}state-exams-group/create`)}
            size="small"
            label="Създай групов изпит"
          />
        </SecuredComponent>
        {/* single exams */}
        <SecuredComponent permission={permissions.canCreateSingleStateExam}>
          <Button
            startIcon={<AddIcon />}
            onClick={() => navigate(`/${GraduationServicePath}state-exams-single/create`)}
            size="small"
            label="Създай единичен изпит"
          />
        </SecuredComponent>
      </>
    </TopToolbar>
  );
};

const ListInternshipStateExams = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const internship = useInternshipFilter({ filters });

  const handleSearch = selectedFilters => {
    setFilters(selectedFilters);
  };

  return (
    <>
      {!filters && <Title title={"Държавни изпити"} />}
      <CustomFilter
        title="Избери филтри"
        cardWidth={isMobile ? "100%" : "700px"}
        filters={internship.fields}
        buttonLabel="Покажи"
        buttonDisabled={!internship.isComplete}
        buttonOnClick={() => handleSearch(internship.query)}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за поток за преддипломен стаж или създай нов
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <SecuredComponent permission={permissions.canCreateGroupStateExams}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/${GraduationServicePath}state-exams-group/create`)}
                  size="small"
                  variant="outlined"
                  label="Създай групов изпит"
                />
              </SecuredComponent>

              <SecuredComponent permission={permissions.canCreateSingleStateExam}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/${GraduationServicePath}state-exams-single/create`)}
                  size="small"
                  variant="outlined"
                  label="Създай единичен изпит"
                />
              </SecuredComponent>
            </Box>
          </Box>
        </Paper>
      ) : (
        <List
          exporter={false}
          title="Държавни изпити"
          empty={false}
          filter={filters}
          actions={<ListActions />}
          hasCreate={false}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Стажантска сесия", value: <InternshipSessionField /> }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Държавен изпит",
                        value: (
                          <ReferenceField
                            source="stateExamId"
                            reference={administrationRequests.getStateExamsSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <div>
                  <MobileListRow
                    fields={[{ label: "Тип", value: <SelectField source="type" choices={StateExamTypeChoices} /> }]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Тип назначение",
                        value: (
                          <SelectField
                            source="assignmentType"
                            choices={StateExamAssignmentTypeChoices}
                            optionText={rec => rec.name}
                          />
                        ),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Дата и час на изпит",
                        value: new Date(record.examTimestamp).toLocaleString("bg-BG"),
                      },
                    ]}
                  />
                  <MobileListRow
                    fields={[
                      {
                        label: "Зала",
                        value: <RoomField render={rec => `${rec?.roomName} - ${rec?.buildingName} (${rec?.capacityExam})`} />,
                      },
                    ]}
                  />
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    <SecuredComponent permission={permissions.canPreviewStateExamResults}>
                      <Button
                        startIcon={<GradingIcon />}
                        onClick={() => navigate(`/${GraduationServicePath}state-exams-results/${record?.id}`)}
                        size="small"
                        label="Резултати"
                      />
                    </SecuredComponent>
                    {record?.type === StateExamTypes.Group && (
                      <SecuredComponent permission={permissions.canCreateGroupStateExams}>
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() =>
                            navigate(`/${GraduationServicePath}state-exams-group-add/${record?.groupId}/create`)
                          }
                          size="small"
                          label="Добави изпит"
                        />
                      </SecuredComponent>
                    )}
                  </div>
                </div>
              )}
              tertiaryText={() => (
                <SecuredComponent permission={permissions.canPreviewStateExams}>
                  <ShowButton label="" />
                </SecuredComponent>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
            >
              <InternshipSessionField />
              <ReferenceField
                source="stateExamId"
                reference={administrationRequests.getStateExamsSelect()}
                label="Държавен изпит"
              >
                <TextField source="name" />
              </ReferenceField>

              <SelectField
                label="Тип"
                source="type"
                choices={StateExamTypeChoices}
              />
              <SelectField
                label="Тип назначение"
                source="assignmentType"
                choices={StateExamAssignmentTypeChoices}
                optionText={rec => rec.name}
              />
              <FunctionField
                label="Дата и час на изпит"
                render={rec => new Date(rec.examTimestamp).toLocaleString("bg-BG")}
              />
              <RoomField
                label="Зала"
                render={rec => `${rec?.roomName} - ${rec?.buildingName} (${rec?.capacityExam})`}
              />
              <SecuredComponent permission={permissions.canPreviewStateExams}>
                <ShowButton />
              </SecuredComponent>

              <FunctionField
                render={rec => (
                  <SecuredComponent permission={permissions.canPreviewStateExamResults}>
                    <Button
                      startIcon={<GradingIcon />}
                      onClick={() => navigate(`/${GraduationServicePath}state-exams-results/${rec?.id}`)}
                      size="small"
                      label="Резултати"
                    />
                  </SecuredComponent>
                )}
              />
              <FunctionField
                render={rec =>
                  rec?.type === StateExamTypes.Group && (
                    <SecuredComponent permission={permissions.canCreateGroupStateExams}>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => navigate(`/${GraduationServicePath}state-exams-group-add/${rec?.groupId}/create`)}
                        size="small"
                        label="Добави изпит"
                      />
                    </SecuredComponent>
                  )
                }
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListInternshipStateExams;
